import { cn } from '@workspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { OTPInput, OTPInputContext } from 'input-otp'
import { MinusIcon } from 'lucide-react'
import * as React from 'react'

const inputOTPVariants = cva('', {
  variants: {
    size: {
      sm: '',
      md: '',
      lg: '',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

const InputOTPSizeContext = React.createContext<VariantProps<typeof inputOTPVariants>>({
  size: 'md',
})

interface InputOTPProps {
  maxLength: number
  value?: string
  onChange?: (value: string) => void
  onComplete?: (value: string) => void
  disabled?: boolean
  containerClassName?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
  children: React.ReactNode
}

function InputOTP({
  className,
  containerClassName,
  size = 'md',
  maxLength,
  value,
  onChange,
  onComplete,
  disabled,
  children,
}: InputOTPProps) {
  return (
    <InputOTPSizeContext.Provider value={{ size }}>
      <OTPInput
        data-slot="input-otp"
        maxLength={maxLength}
        value={value}
        onChange={onChange}
        onComplete={onComplete}
        disabled={disabled}
        containerClassName={cn(
          'flex items-center gap-2 has-disabled:opacity-60',
          containerClassName
        )}
        className={cn('disabled:cursor-not-allowed', className)}
      >
        {children}
      </OTPInput>
    </InputOTPSizeContext.Provider>
  )
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="input-otp-group" className={cn('flex items-center', className)} {...props} />
  )
}

const inputOTPSlotVariants = cva(
  'data-[active=true]:border-[rgb(249,115,22)] data-[active=true]:ring-[rgb(249,115,22)]/50 data-[active=true]:aria-invalid:ring-destructive/20 dark:data-[active=true]:aria-invalid:ring-destructive/40 aria-invalid:border-destructive data-[active=true]:aria-invalid:border-destructive bg-input border-border text-foreground relative flex items-center justify-center border-y border-r shadow-xs transition-all outline-none first:rounded-l-md first:border-l last:rounded-r-md data-[active=true]:z-10 data-[active=true]:ring-[3px]',
  {
    variants: {
      size: {
        sm: 'h-8 w-8 text-xs',
        md: 'h-10 w-10 text-sm',
        lg: 'h-14 w-14 text-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

function InputOTPSlot({
  index,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  index: number
}) {
  const inputOTPContext = React.useContext(OTPInputContext)
  const sizeContext = React.useContext(InputOTPSizeContext)
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {}

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={cn(inputOTPSlotVariants({ size: sizeContext.size }), className)}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink bg-white h-4 w-px duration-1000" />
        </div>
      )}
    </div>
  )
}

function InputOTPSeparator({ ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="input-otp-separator" {...props}>
      <MinusIcon />
    </div>
  )
}

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator }
